const API_BASE = (import.meta as any).env?.VITE_API_BASE || ((import.meta as any).env?.PROD ? '' : 'http://localhost:8788');

export async function getMe() {
  const r = await fetch(`${API_BASE}/api/me`, { 
    credentials: 'include' // Include cookies
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function logout() {
  const r = await fetch(`${API_BASE}/api/auth/logout`, { 
    method: 'POST',
    credentials: 'include' // Include cookies
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function loginWithEmail(email: string, password: string) {
  const r = await fetch(`${API_BASE}/api/auth/login`, { 
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password })
  });
  if (!r.ok) {
    const error = await r.json();
    throw new Error(error.error || 'Login failed');
  }
  return r.json();
}

export async function signupWithEmail(email: string, password: string, name: string) {
  const r = await fetch(`${API_BASE}/api/auth/signup`, { 
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, name })
  });
  if (!r.ok) {
    const error = await r.json();
    throw new Error(error.error || 'Signup failed');
  }
  return r.json();
}

// YouTube API functions
export async function ytConnected() {
  const r = await fetch(`${API_BASE}/api/youtube/connected`, { 
    credentials: 'include' 
  });
  if (!r.ok) {
    if (r.status === 401) {
      // Return disconnected state for 401 instead of throwing
      return { connected: false, reauth_required: true };
    }
    throw new Error(await r.text());
  }
  return r.json();
}

export async function ytMe() {
  const r = await fetch(`${API_BASE}/api/youtube/me`, { 
    credentials: 'include' 
  });
  if (!r.ok) {
    if (r.status === 401) {
      // Return a special object for 401 instead of throwing
      return { reauth_required: true, error: await r.text() };
    }
    throw new Error(await r.text());
  }
  return r.json();
}

export async function ytUploads(playlistId: string, pageToken?: string) {
  const params = new URLSearchParams({ 
    playlistId, 
    max: '24' 
  });
  if (pageToken) params.set('pageToken', pageToken);
  
  const r = await fetch(`${API_BASE}/api/youtube/uploads?${params}`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Dashboard alerts API
export async function getDashboardAlerts(hours: number = 24, limit: number = 50) {
  const params = new URLSearchParams({
    userId: 'me',
    hours: hours.toString(),
    limit: limit.toString()
  });
  
  const r = await fetch(`${API_BASE}/api/dashboard/alerts?${params}`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getRandomVerse(userId: string) {
  const r = await fetch(`${API_BASE}/api/verses/random?userId=${encodeURIComponent(userId)}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function postSpecificVerse(payload: { userId: string; book: string; chapter: number; verse: string; }) {
  const r = await fetch(`${API_BASE}/api/verses/specific`, {
    method: 'POST',
    headers: {'content-type':'application/json'},
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function postGenerateTale(payload: { userId: string; book: string; chapter: number; verse: string; theme?: string; targetWords?: number; }) {
  const r = await fetch(`${API_BASE}/api/tales/generate`, {
    method: 'POST',
    headers: {'content-type':'application/json'},
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function listTales(userId: string) {
  const r = await fetch(`${API_BASE}/api/tales?userId=${encodeURIComponent(userId)}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getTale(id: string) {
  const r = await fetch(`${API_BASE}/api/tales/${id}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function exportTale(id: string) {
  const r = await fetch(`${API_BASE}/api/tales/${id}/export`);
  if (!r.ok) throw new Error(await r.text());
  return r.text();
}

// Phase 3B: Image Generation APIs
export async function createImagePrompts(payload: {
  taleId: string;
  scenes?: number;
  sceneVariations?: number;
  thumbs?: number;
  styleNote?: string;
}) {
  const r = await fetch(`${API_BASE}/api/images/prompts`, {
    method: 'POST',
    headers: {'content-type':'application/json'},
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function listImagesForTale(taleId: string) {
  const r = await fetch(`${API_BASE}/api/images/${taleId}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getImageStatus(id: string) {
  const r = await fetch(`${API_BASE}/api/images/status/${id}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export function imageFileUrl(id: string) {
  return `${API_BASE}/api/images/file/${id}`;
}

// Phase 4A: Audio Upload APIs
export async function uploadNarrationFile(taleId: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API_BASE}/api/uploads/narration?taleId=${encodeURIComponent(taleId)}`, {
    method: 'POST', body: fd
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function uploadMusicFile(taleId: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API_BASE}/api/uploads/music?taleId=${encodeURIComponent(taleId)}`, {
    method: 'POST', body: fd
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Phase 4B: Storyboard APIs
export async function getStoryboard(taleId: string) {
  const r = await fetch(`${API_BASE}/api/storyboards/${taleId}`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function saveStoryboard(taleId: string, payload: {
  slides: Array<{ imageId: string; durationSec: number; captionEn?: string; captionFr?: string }>;
  meta: {
    primaryThumbImageId?: string;
    title_en?: string; title_fr?: string;
    description_en?: string; description_fr?: string;
    tags?: string[];
    narrationUploadId?: string;
    musicUploadId?: string;
    musicGainDb?: number;
    duckOnNarration?: boolean;
  };
}) {
  const r = await fetch(`${API_BASE}/api/storyboards/${taleId}`, {
    method: 'POST',
    headers: {'content-type':'application/json'},
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Phase 5A-Manual: Manual Upload APIs
export async function getStudioUrl(userId: string, channelId?: string) {
  const r = await fetch(`${API_BASE}/api/youtube/manual/start?userId=${encodeURIComponent(userId)}${channelId ? `&channelId=${encodeURIComponent(channelId)}` : ''}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function listRecentUploads(userId: string, max = 10) {
  const r = await fetch(`${API_BASE}/api/youtube/manual/recent?userId=${encodeURIComponent(userId)}&max=${max}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function bindVideo(userId: string, taleId: string, videoId: string) {
  const r = await fetch(`${API_BASE}/api/youtube/manual/bind`, {
    method: 'POST',
    headers: { 'content-type':'application/json' },
    body: JSON.stringify({ userId, taleId, videoId })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function scheduleVideo(userId: string, taleId: string, youtubeVideoId: string, publishLocalISO: string, tz: string, offsetMinutes: number) {
  const r = await fetch(`${API_BASE}/api/youtube/schedule`, {
    method: 'POST',
    headers: { 'content-type':'application/json' },
    body: JSON.stringify({ userId, taleId, youtubeVideoId, publishLocalISO, timeZone: tz, offsetMinutes })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// local tz helper
export function getLocalTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

// Phase 5B: Status Tracking APIs
export async function getYoutubeStatus(taleId: string) {
  const r = await fetch(`${API_BASE}/api/youtube/status/tale/${taleId}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getYoutubeHistory(taleId: string) {
  const r = await fetch(`${API_BASE}/api/youtube/history/tale/${taleId}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Phase 6A: Dashboard APIs
export async function fetchSummary() {
  const r = await fetch(`${API_BASE}/api/dashboard/summary`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function fetchTalesStatus(params?: { q?: string; status?: string; limit?: number }) {
  const q = new URLSearchParams();
  if (params?.q) q.set('q', params.q);
  if (params?.status) q.set('status', params.status);
  if (params?.limit) q.set('limit', String(params.limit));
  const r = await fetch(`${API_BASE}/api/tales/status?${q.toString()}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function fetchRecentEvents(limit = 50, type?: string) {
  const q = new URLSearchParams({ limit: String(limit) });
  if (type) q.set('type', type);
  const r = await fetch(`${API_BASE}/api/dashboard/recent?${q.toString()}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Phase 6C: Alerts & Error Monitoring
export async function fetchAlerts(userId: string, hours = 24, limit = 50) {
  const q = new URLSearchParams({ userId, hours: String(hours), limit: String(limit) });
  const r = await fetch(`${API_BASE}/api/dashboard/alerts?${q.toString()}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function markAlertsSeen(userId: string) {
  const r = await fetch(`${API_BASE}/api/dashboard/alerts/seen`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ userId })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Dev-only: Generate test errors for alerts testing
export async function devGenerateError(payload: any) {
  const r = await fetch(`${API_BASE}/api/dev/generate-error`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Phase 7: YouTube Optimization API functions
export async function ytVideo(id: string) {
  const r = await fetch(`${API_BASE}/api/youtube/video?id=${encodeURIComponent(id)}`, { 
    credentials: 'include' 
  });
  if (!r.ok) throw new Error(await r.text()); 
  return r.json();
}

export async function ytVideoAnalytics(id: string, days = 28) {
  const r = await fetch(`${API_BASE}/api/yt/analytics/video?id=${encodeURIComponent(id)}&days=${days}`, { 
    credentials: 'include' 
  });
  if (!r.ok) throw new Error(await r.text()); 
  return r.json();
}

export async function ytVideoComments(id: string, limit = 50) {
  const r = await fetch(`${API_BASE}/api/youtube/comments?id=${encodeURIComponent(id)}&limit=${limit}`, { 
    credentials: 'include' 
  });
  if (!r.ok) throw new Error(await r.text()); 
  return r.json();
}

export async function optimizeSuggest(payload: any) {
  const r = await fetch(`${API_BASE}/api/optimize/suggest`, { 
    method: 'POST', 
    headers: { 'content-type': 'application/json' }, 
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text()); 
  return r.json();
}

export async function aiSuggestVideo(payload: {
  title: string;
  description?: string;
  tags?: string[];
  mode?: 'seo' | 'viral' | 'detailed' | 'all';
}) {
  const r = await fetch(`${API_BASE}/api/ai/suggest-video`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function ytVideoUpdate(payload: { 
  videoId: string; 
  title?: string; 
  description?: string; 
  tags?: string[]; 
  defaultLanguage?: string; 
  notes?: string 
}) {
  const r = await fetch(`${API_BASE}/api/youtube/video:update`, { 
    method: 'POST', 
    headers: { 'content-type': 'application/json' }, 
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text()); 
  return r.json();
}

// Thumbnail generation API functions
export async function thumbsGenerate(videoId: string, prompts: string[]) {
  const r = await fetch(`${API_BASE}/api/optimize/thumbnails/generate`, {
    method: 'POST', 
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ videoId, prompts }),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function thumbsList(videoId: string) {
  const r = await fetch(`${API_BASE}/api/optimize/thumbnails/list?videoId=${encodeURIComponent(videoId)}`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// AI Script Generation API functions (Phase 8)
export interface ScriptGenerationRequest {
  topic: string;
  duration: number;
  audience?: string;
  style?: string;
  referenceScriptId?: string;
}

export interface ScriptMetadata {
  title: string;
  description: string;
  tags: string[];
  estimatedDuration: string;
  retentionScore: number;
  retentionReasoning: string;
  complexity: 'Beginner' | 'Intermediate' | 'Advanced';
}

export interface ScriptSection {
  timestamp: string;
  title: string;
  content: string;
}

export interface ProductionNotes {
  requiredAssets: string[];
  estimatedProductionTime: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  specialRequirements: string[];
  brollSuggestions: string[];
}

export interface ScriptScene {
  sceneNumber: number;
  title: string;
  visualDescription: string;
  characters: string[];
  setting: string;
  mood: string;
  timestamp: string;
  scriptContent: string;
  suggestedAnimation: 'zoom-in' | 'pan-left' | 'pan-right' | 'zoom-out' | 'static';
  imagePrompt?: string;
  imageUrl?: string;
}

export interface ScriptResponse {
  success: boolean;
  script: {
    sections: ScriptSection[];
  };
  scenes?: ScriptScene[];
  metadata: ScriptMetadata;
  productionNotes: ProductionNotes;
}

export interface RegenerationRequest {
  projectId: string;
  originalScript: any;
  instruction?: string;
  improveType?: 'hook' | 'pacing' | 'visuals' | 'tone' | 'overall';
}

export interface QuickImproveRequest {
  projectId: string;
  originalScript: any;
  improveType: 'hook' | 'pacing' | 'visuals' | 'tone' | 'overall';
}

export async function generateScript(request: ScriptGenerationRequest, projectId?: string): Promise<ScriptResponse> {
  const url = projectId 
    ? `${API_BASE}/api/ai/generate-script?projectId=${encodeURIComponent(projectId)}`
    : `${API_BASE}/api/ai/generate-script`;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function regenerateScript(request: RegenerationRequest): Promise<ScriptResponse & {
  improvement: {
    type: string;
    userInstruction?: string;
    retentionImprovement: number;
  };
}> {
  const r = await fetch(`${API_BASE}/api/ai/regenerate-script`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function quickImproveScript(request: QuickImproveRequest): Promise<ScriptResponse & {
  improvement: {
    type: string;
    retentionImprovement: number;
  };
}> {
  const r = await fetch(`${API_BASE}/api/ai/quick-improve`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function getUserAIPreferences() {
  const r = await fetch(`${API_BASE}/api/ai/user-preferences`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function recordScriptInteraction(interaction: {
  projectId: string;
  interactionType: 'loved' | 'regenerated' | 'exported' | 'deleted';
  feedbackInstruction?: string;
}) {
  const r = await fetch(`${API_BASE}/api/ai/script-interactions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(interaction),
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function fetchVideoProjects() {
  const r = await fetch(`${API_BASE}/api/video-projects`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  const response = await r.json();
  // Extract data from response wrapper
  return response.data || response;
}

export async function fetchVideoProject(id: string) {
  const r = await fetch(`${API_BASE}/api/video-projects/${id}`, {
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  const response = await r.json();
  // Extract project data from response wrapper
  return response.data || response;
}

export async function deleteVideoProject(id: string) {
  const r = await fetch(`${API_BASE}/api/video-projects/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMultipleVideoProjects(ids: string[]) {
  const r = await fetch(`${API_BASE}/api/video-projects/bulk/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ids })
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
